import React from 'react';
import { RequestStatus } from '../../../../../../../../../shared/enums/request-status';
import { ResponseErrorWithHandled } from '../../../../../../../../../shared/types';
import { RequestState } from '../../../../../../../../../shared/types/request-state';
import {
  AttachmentDeletePayload,
  AttachmentUploadPayload,
  SelectedProspectData,
} from '../../../../../../../../sequence/types';
import { Field } from '../../../../../../../../settings/types/field';
import {
  AttachmentUploadResponse,
  EmailDraftDataProps,
  GetProspectEmailsTabList,
  ReplyDraftDiscardPayload,
  ReplyDraftPayload,
  ReplyEmailPayload,
  SendEmailProspectTabProps,
} from '../../../../../../../../unified-inbox/type';
import {
  EmailAccountMinimalListResponse,
  getMinimalEmailAccountListProps,
} from '../../../../../../../types';

export type EmailListFromOpt = {
  id: number;
  name: string;
  email: string;
  emailServiceProvider?: string;
  status?: number;
};

export type EmailsTabListing = {
  page: number;
  limit: number;
  emailId?: number;
};
export type ProspectEmailListArgs = EmailsTabListing & {
  prospectId: number;
};
export type EmailListTabProps = {
  fetchFields?: () => void;
  fields?: Field[];
  fetchEmailListData?: (payload: ProspectEmailListArgs) => void;
  emailListItems?: GetProspectEmailsTabList;
  selectedProspectId?: number;
  selectedProspectData?: SelectedProspectData;
  sendEmailProspectTab?: (payload: SendEmailProspectTabProps) => void;
  deleteProspectEmailRequests?: (
    emailId: number,
    shouldThreadDelete?: boolean,
  ) => void;
  resetGetProspectEmailsTabListingRequests?: () => void;
  resetDeleteProspectEmailRequests?: () => void;
  draftProspectEmailRequest?: (payload: ReplyDraftPayload) => void;
  draftProspectEmailRequestStatus?: RequestStatus;
  fetchEmailListDataStatus?: RequestStatus;
  deleteProspectEmailStatus?: RequestStatus;
  emailDraftData?: EmailDraftDataProps;
  getEmailAccounts?: (filter: getMinimalEmailAccountListProps) => void;
  emailAccount?: EmailAccountMinimalListResponse;
  sendEmailProspectTabStatus?: RequestState;
  resetSendEmailProspectTabRequests?: () => void;
  sendReplyDraftDiscardRequest?: (payload: ReplyDraftDiscardPayload) => void;
  resetReplyForwardDraftRequests?: (emailId: number) => void;
  replyDraftDiscardRequestStatus?: { [id: number]: RequestState };
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  totalItems?: number;
  page?: number;
  pageLimit?: number;
  resetEmailDraftDataRequest?: () => void;
  resetEmailDraftDataRequestStatus?: RequestStatus;

  handleSendAttachmentUploadRequest?: (
    payload: AttachmentUploadPayload,
  ) => void;
  handleSendDeleteAttachmentRequest?: (
    payload: AttachmentDeletePayload,
  ) => void;
  handleResetUploadAttachmentRequest?: () => void;
  handleResetDeleteAttachmentRequest?: () => void;
  uploadAttachmentRequestStatus?: RequestStatus;
  uploadAttachmentRequestError?: ResponseErrorWithHandled;
  uploadedAttachment?: AttachmentUploadResponse;
  deleteAttachmentRequestStatus?: RequestStatus;
  deleteAttachmentRequestError?: ResponseErrorWithHandled;
  deletedAttachmentId?: number;
  singleProspectReplyEmailRequest?: (payload: ReplyEmailPayload) => void;
  setSelectedRepliedEmailId?: (emailId: number) => void;
  replyEmailRequestStatus?: RequestState;
  resetReplyDraftDiscard?: (emailId: number) => void;
  sendGetEmailThreadActivity?: (payload: any) => void;
  resetUnifiedInboxEmailThreadActivity?: () => void;
  unifiedInboxEmailThreadActivity?: any[];
  getEmailThreadActivityRequestStatus?: RequestStatus;
  getEmailThreadActivityRequestError?: any;
  initialShowEmailEditor?: boolean;
  onEmailEditorClose?: () => void;
  handleImageUploadRequest?: (
    formData: any,
    onUploadProgress: any,
  ) => Promise<any>;
  emailRequestStatus?: RequestStatus;
  onActivityChange?: (value: boolean) => void;
  activeTab?: string;
  prospectActivityCount?: any;
  editProspectAcitivityCount?: (payload: any) => void;
  isDemoAccount?: boolean;
};

export type InlineEditorState = {
  type: 'reply' | 'forward';
  emailId: number;
  emailThreadId?: number;
  hashId?: string;
  subject: string;
  to: string;
  unifiedScheduledId?: number;
};

export const emailAddressessOption = [
  { id: 1, name: 'Alice', email: 'alice@gmail.com' },
  { id: 2, name: 'Mickey', email: 'mick@gmail.com' },
  { id: 3, name: 'Lewis Hamilton', email: 'lewis@gmail.com' },
  { id: 4, name: 'Max Verstappen', email: 'max@gmail.com' },
  { id: 5, name: 'Leo', email: 'leo@gmail.com' },
];

export const formatDateTime = (
  dateInput: Date | string = new Date(),
  timeZone = 'Asia/Kolkata',
): string => {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone,
  };

  const formatter = new Intl.DateTimeFormat('en-US', options);
  const parts = formatter.formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value;

  const weekday = get('weekday');
  const month = get('month');
  const day = get('day');
  const year = get('year');
  const hour = get('hour');
  const minute = get('minute');
  const dayPeriod = get('dayPeriod')?.toUpperCase();

  // Get GMT Offset
  const offsetMinutes = -date?.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMinutes);
  const hours = String(Math.floor(abs / 60)).padStart(2, '0');
  const mins = String(abs % 60).padStart(2, '0');

  const gmtOffset = `GMT${sign}${hours}:${mins}`;

  return `${weekday}, ${month} ${day}, ${year} at ${hour}:${minute} ${dayPeriod} (${gmtOffset}) ${timeZone}`;
};

/**
 * Converts HTML content to plain text with proper formatting for lists
 * - Ordered lists (<ol>) are converted to numbered format (1., 2., 3., ...)
 * - Unordered lists (<ul>) are converted to bullet format (•)
 * - Preserves line breaks and removes HTML tags
 */
export const convertHtmlToPlainTextForCopy = (htmlContent: string): string => {
  if (!htmlContent) return '';

  let processedContent = htmlContent;

  // Process ordered lists first - replace <ol> blocks with numbered items
  processedContent = processedContent.replace(
    /<ol[^>]*>([\s\S]*?)<\/ol>/gi,
    (match, listContent) => {
      // Extract all <li> items from this ordered list
      const listItems = listContent.match(/<li[^>]*>([\s\S]*?)<\/li>/gi) || [];
      // Convert each <li> to numbered format
      const numberedItems = listItems.map((item, index) => {
        // Extract text content from <li>
        const itemContent = item.replace(/<li[^>]*>|<\/li>/gi, '');
        // Remove nested HTML tags temporarily to get clean text
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = itemContent;
        const text = (tempDiv.textContent || tempDiv.innerText || '').trim();
        return `\n${index + 1}. ${text}`;
      });
      return numberedItems.join('');
    },
  );

  // Process unordered lists - replace <ul> blocks with bullet points
  processedContent = processedContent.replace(
    /<ul[^>]*>([\s\S]*?)<\/ul>/gi,
    (match, listContent) => {
      // Extract all <li> items from this unordered list
      const listItems = listContent.match(/<li[^>]*>([\s\S]*?)<\/li>/gi) || [];
      // Convert each <li> to bullet format
      const bulletItems = listItems.map((item) => {
        // Extract text content from <li>
        const itemContent = item.replace(/<li[^>]*>|<\/li>/gi, '');
        // Remove nested HTML tags temporarily to get clean text
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = itemContent;
        const text = (tempDiv.textContent || tempDiv.innerText || '').trim();
        return `\n• ${text}`;
      });
      return bulletItems.join('');
    },
  );

  // Convert <br> and <br/> tags to newlines
  processedContent = processedContent.replace(/<br\s*\/?>/gi, '\n');

  // Convert <div> tags to newlines (block elements)
  processedContent = processedContent.replace(/<div[^>]*>/gi, '\n');
  processedContent = processedContent.replace(/<\/div>/gi, '\n');

  // Convert <p> tags to newlines
  processedContent = processedContent.replace(/<p[^>]*>/gi, '\n');
  processedContent = processedContent.replace(/<\/p>/gi, '\n');

  // Remove all remaining HTML tags
  processedContent = processedContent.replace(/<[^>]*>/g, '');

  // Decode HTML entities
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = processedContent;
  let text = tempDiv.textContent || tempDiv.innerText || '';

  // Clean up multiple consecutive newlines (max 2 consecutive)
  text = text.replace(/\n{3,}/g, '\n\n');

  // Trim leading and trailing whitespace/newlines
  text = text.trim();

  return text;
};

/**
 * Formats an array or single value to show first item with "+N" indicator for remaining items
 * @param value - Can be an array or a single value (string, number, etc.)
 * @returns React node showing first item and "+N" if there are more items
 */
export const formatArrayWithCount = (
  value: string | string[] | number | number[] | any,
): React.ReactNode => {
  if (!value) return null;

  // If it's an array with items
  if (Array.isArray(value) && value.length > 0) {
    return React.createElement(
      React.Fragment,
      null,
      value[0],
      value.length > 1 &&
        React.createElement('span', null, ` +${value.length - 1}`),
    );
  }

  // If it's not an array, return as is
  return value;
};
